import { Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  RouterProvider,
  Route,
  Navigate,
  Outlet,
} from "react-router-dom";
import { ProtectedRoute } from "./ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { DomainFeatureRoute } from "@/components/auth/DomainFeatureRoute";
import { RouteErrorBoundary } from "./RouteErrorBoundary";
// lazy() is wrapped to auto-reload on stale-chunk failures after a deploy
// (see @/lib/lazy). Import from here instead of "react".
import { lazy } from "@/lib/lazy";

// Auth pages are imported eagerly so they're always available offline
// (lazy chunks require a network fetch if not already cached)
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import OAuthCallbackPage from "@/pages/auth/OAuthCallbackPage";

// Lazy load pages for code splitting
const ForgotPasswordPage = lazy(
  () => import("@/pages/auth/ForgotPasswordPage"),
);
const DashboardPage = lazy(() => import("@/pages/dashboard/DashboardPage"));
const VMsPage = lazy(() => import("@/pages/resources/VMsPage"));
// StoragePage removed - functionality merged into DisksListPage
// NetworksPage removed - functionality merged into NetworksListPage
const DisksListPage = lazy(() => import("@/pages/resources/DisksListPage"));
const NetworksListPage = lazy(
  () => import("@/pages/resources/NetworksListPage"),
);
const CreateVMWizardPage = lazy(
  () => import("@/pages/resources/CreateVMWizardPage"),
);
const ResourceRenewalCheckoutPage = lazy(
  () => import("@/pages/resources/ResourceRenewalCheckoutPage"),
);
const StorageListPage = lazy(() => import("@/pages/resources/StorageListPage"));
const StorageDetailPage = lazy(
  () => import("@/pages/resources/StorageDetailPage"),
);
const CreateStorageWizardPage = lazy(
  () => import("@/pages/resources/CreateStorageWizardPage"),
);
const ResourceDetailPage = lazy(
  () => import("@/pages/resources/ResourceDetailPage"),
);
const DiskDetailPage = lazy(() => import("@/pages/resources/DiskDetailPage"));
const NetworkDetailPage = lazy(
  () => import("@/pages/resources/NetworkDetailPage"),
);
const BillingPage = lazy(() => import("@/pages/billing/BillingPage"));
const BalanceOverviewPage = lazy(
  () => import("@/pages/billing/BalanceOverviewPage"),
);
const DepositPage = lazy(() => import("@/pages/billing/DepositPage"));
const PaymentStatusPage = lazy(
  () => import("@/pages/billing/PaymentStatusPage"),
);
const PaymentHistoryPage = lazy(
  () => import("@/pages/billing/PaymentHistoryPage"),
);
const InvoicesPage = lazy(() => import("@/pages/billing/InvoicesPage"));
const PaymentMethodsPage = lazy(
  () => import("@/pages/billing/PaymentMethodsPage"),
);
const ConnectedAccountsPage = lazy(
  () => import("@/pages/account/ConnectedAccountsPage"),
);
const ApiKeysPage = lazy(() => import("@/pages/api-keys/ApiKeysPage"));
// Orders pages
const OrdersListPage = lazy(() => import("@/pages/orders/OrdersListPage"));
const OrderDetailPage = lazy(() => import("@/pages/orders/OrderDetailPage"));
// Settings pages
const ProfilePage = lazy(() => import("@/pages/settings/ProfilePage"));
const SecurityPage = lazy(() => import("@/pages/settings/SecurityPage"));
const PreferencesPage = lazy(() => import("@/pages/settings/PreferencesPage"));
const NotificationsPage = lazy(
  () => import("@/pages/settings/NotificationsPage"),
);
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));
const BillingInfoPage = lazy(() => import("@/pages/settings/BillingInfoPage"));
const LogsPage = lazy(() => import("@/pages/logs/LogsPage"));

// Domain purchase pages
const DomainSearchPage = lazy(() => import("@/pages/domains/DomainSearchPage"));
const DomainCheckoutPage = lazy(
  () => import("@/pages/domains/DomainCheckoutPage"),
);
const DomainManagementPage = lazy(
  () => import("@/pages/domains/DomainManagementPage"),
);
const DomainDetailPage = lazy(() => import("@/pages/domains/DomainDetailPage"));
const DomainTransferInPage = lazy(
  () => import("@/pages/domains/DomainTransferInPage"),
);

function LoadingFallback() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-canvas">
      <div className="h-4 w-4 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      <span className="text-xs text-muted font-mono">Loading...</span>
    </div>
  );
}

// createBrowserRouter enables useBlocker for navigation confirmation dialogs.
// BrowserRouter (declarative mode) does not support useBlocker.
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route
      errorElement={<RouteErrorBoundary />}
      element={
        <Suspense fallback={<LoadingFallback />}>
          <Outlet />
        </Suspense>
      }
    >
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      {/* OAuth2 callback route (unified for all providers) */}
      <Route path="/auth/oauth2/callback" element={<OAuthCallbackPage />} />

      {/* Protected routes with layout */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<DashboardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        {/* Resource routes - ORDER MATTERS: specific before dynamic */}
        <Route path="/resources/vms" element={<VMsPage />} />
        <Route path="/resources/networks" element={<NetworksListPage />} />
        <Route path="/resources/disks" element={<DisksListPage />} />
        <Route path="/resources/storage" element={<StorageListPage />} />
        <Route
          path="/resources/storage/create"
          element={<CreateStorageWizardPage />}
        />
        <Route
          path="/resources/storage/:resourceId"
          element={<StorageDetailPage />}
        />
        <Route path="/resources/create" element={<CreateVMWizardPage />} />
        <Route
          path="/resources/:id/renew"
          element={<ResourceRenewalCheckoutPage />}
        />
        {/* Specific detail pages for disk and network resources */}
        <Route
          path="/resources/disks/:resourceId"
          element={<DiskDetailPage />}
        />
        <Route
          path="/resources/networks/:resourceId"
          element={<NetworkDetailPage />}
        />
        {/* Generic detail page for VMs and other resource types */}
        <Route path="/resources/:resourceId" element={<ResourceDetailPage />} />
        {/* Orders routes */}
        <Route path="/orders" element={<OrdersListPage />} />
        <Route path="/orders/:orderId" element={<OrderDetailPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/billing/balance" element={<BalanceOverviewPage />} />
        {/* One deposit screen, one code path. /billing/add-balance was a second, simpler
            implementation that had already drifted — it opened QR orders without a provider,
            skipped the bank-account lookup and had no proof-upload step. Redirected rather than
            deleted outright so an existing bookmark still lands somewhere that works. */}
        <Route
          path="/billing/add-balance"
          element={<Navigate to="/billing/deposit" replace />}
        />
        <Route path="/billing/deposit" element={<DepositPage />} />
        <Route
          path="/billing/payment/:paymentId"
          element={<PaymentStatusPage />}
        />
        <Route
          path="/billing/payment-history"
          element={<PaymentHistoryPage />}
        />
        <Route path="/billing/invoices" element={<InvoicesPage />} />
        <Route
          path="/billing/payment-methods"
          element={<PaymentMethodsPage />}
        />
        {/* Settings routes - nested with default redirect */}
        <Route path="/settings">
          <Route index element={<Navigate to="/settings/profile" replace />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="preferences" element={<PreferencesPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="api-keys" element={<ApiKeysPage />} />
          <Route
            path="e-invoice"
            element={<Navigate to="/settings/billing-info" replace />}
          />
          <Route path="billing-info" element={<BillingInfoPage />} />
        </Route>
        {/* Logs route */}
        <Route path="/logs" element={<LogsPage />} />
        {/* Domain routes - guarded by feature flag - ORDER MATTERS: specific before dynamic */}
        <Route element={<DomainFeatureRoute />}>
          <Route
            path="/domains"
            element={<Navigate to="/domains/search" replace />}
          />
          <Route path="/domains/search" element={<DomainSearchPage />} />
          <Route path="/domains/checkout" element={<DomainCheckoutPage />} />
          <Route path="/domains/manage" element={<DomainManagementPage />} />
          <Route
            path="/domains/transfer-in"
            element={<DomainTransferInPage />}
          />
          <Route path="/domains/:id" element={<DomainDetailPage />} />
        </Route>
        {/* Legacy routes for backward compatibility */}
        <Route
          path="/account/settings"
          element={<Navigate to="/settings/profile" replace />}
        />
        <Route
          path="/account/connected-accounts"
          element={<ConnectedAccountsPage />}
        />
        <Route
          path="/api-keys"
          element={<Navigate to="/settings/api-keys" replace />}
        />
      </Route>

      {/* 404 Not Found */}
      <Route path="*" element={<NotFoundPage />} />
    </Route>,
  ),
);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
